import React from 'react';
import { FaRobot, FaCheck, FaExclamationTriangle, FaSpinner, FaMagic } from 'react-icons/fa';
import './AIAssessmentGenerationModal.css';

const AIAssessmentGenerationModal = ({ 
  isOpen, 
  onClose, 
  isLoading, 
  generationResult, 
  error, 
  onViewTemplate,
  onTryAgain 
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <div className="ai-generation-modal-overlay" onClick={handleBackdropClick}>
      <div className="ai-generation-modal">
        <div className="ai-generation-header">
          <div className="ai-generation-icon">
            <FaMagic />
          </div>
          <h2>AI Assessment Generation</h2>
          {!isLoading && (
            <button className="modal-close-btn" onClick={onClose}>
              &times;
            </button>
          )}
        </div>

        <div className="ai-generation-content">
          {/* Loading State */}
          {isLoading && (
            <div className="generation-loading">
              <div className="loading-animation">
                <FaSpinner className="spinning" />
              </div>
              <h3>Generating Your AI Assessment...</h3>
              <div className="loading-steps">
                <div className="loading-step active">
                  <FaRobot /> Analyzing your requirements
                </div>
                <div className="loading-step active">
                  <FaMagic /> Crafting contextual questions
                </div>
                <div className="loading-step active">
                  <FaCheck /> Optimizing question types
                </div>
              </div>
              <p className="loading-message">
                This may take a few moments as our AI creates personalized assessment questions 
                tailored to your change management needs.
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="generation-error">
              <div className="error-icon">
                <FaExclamationTriangle />
              </div>
              <h3>Generation Failed</h3>
              <p className="error-message">{error}</p>
              <div className="error-actions">
                <button className="btn-secondary" onClick={onClose}>
                  Close
                </button>
                <button className="btn-primary" onClick={onTryAgain}>
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Success State */}
          {generationResult && !isLoading && !error && (
            <div className="generation-success">
              <div className="success-icon">
                <FaCheck />
              </div>
              <h3>Assessment Generated Successfully!</h3>
              
              <div className="generation-summary">
                <div className="summary-card">
                  <h4>📋 Assessment Template Created</h4>
                  <div className="summary-details">
                    <div className="summary-item">
                      <strong>Name:</strong> {generationResult.templateName}
                    </div>
                    <div className="summary-item">
                      <strong>Questions Generated:</strong> {generationResult.questionCount}
                    </div>
                    <div className="summary-item">
                      <strong>Question Types:</strong> {generationResult.questionTypes?.join(', ') || 'Mixed'}
                    </div>
                    <div className="summary-item">
                      <strong>Focus Areas:</strong> {generationResult.focusAreas?.join(', ') || 'Comprehensive'}
                    </div>
                  </div>
                </div>

                {generationResult.sampleQuestions && generationResult.sampleQuestions.length > 0 && (
                  <div className="sample-questions">
                    <h4>🎯 Sample Questions Preview</h4>
                    <div className="questions-preview">
                      {generationResult.sampleQuestions.slice(0, 3).map((question, index) => {
                        // Map technical types to user-friendly names
                        const typeMapping = {
                          'single_select': 'Multiple Choice',
                          'multi_select': 'Multi-Select',
                          'likert': 'Likert Scale',
                          'comfort_scale': 'Comfort Scale',
                          'confidence_scale': 'Confidence Scale',
                          'frequency_scale': 'Frequency Scale',
                          'yes_no': 'Yes/No',
                          'ranking': 'Ranking',
                          'scale': '1-5 Scale',
                          'long_text': 'Long Text',
                          'short_text': 'Short Text'
                        };
                        
                        const displayType = typeMapping[question.type] || question.type;
                        
                        return (
                          <div key={index} className="sample-question">
                            <div className="question-text">{question.text}</div>
                            <div className="question-type">{displayType}</div>
                          </div>
                        );
                      })}
                      {generationResult.sampleQuestions.length > 3 && (
                        <div className="more-questions">
                          +{generationResult.sampleQuestions.length - 3} more questions
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="success-actions">
                <button className="btn-secondary" onClick={onClose}>
                  Close
                </button>
                <button className="btn-primary" onClick={onViewTemplate}>
                  <FaCheck /> View Template
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAssessmentGenerationModal; 